import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Button,
  Group,
  Text,
  SimpleGrid,
  Card,
  Badge,
  Modal,
  Stack,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  IconEdit,
  IconTrash,
  IconPlus,
  IconToggleLeft,
  IconToggleRight,
  IconGripVertical,
} from "@tabler/icons-react";
import {
  GET_PROMOTIONS,
  DELETE_PROMOTION,
  UPDATE_PROMOTION,
  UPDATE_PROMOTIONS_ORDER,
} from "../../gqls/promotion";

export const PromotionsPage = () => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [draggedOverId, setDraggedOverId] = useState(null);
  const [localPromotions, setLocalPromotions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const { data, loading, refetch } = useQuery(GET_PROMOTIONS);
  const [deletePromotion] = useMutation(DELETE_PROMOTION);
  const [updatePromotion] = useMutation(UPDATE_PROMOTION);
  const [updatePromotionsOrder] = useMutation(UPDATE_PROMOTIONS_ORDER);

  const promotions = data?.promotions || [];
  const MAX_PROMOTIONS = 10;
  const canAddMore = promotions.length < MAX_PROMOTIONS;

  useEffect(() => {
    if (promotions.length > 0) {
      const sorted = [...promotions].sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        return orderA - orderB;
      });
      setLocalPromotions(sorted);
      setHasChanges(false);
    }
  }, [promotions]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `до ${day}.${month}.${year}`;
  };

  const parseDescription = (text) => {
    let parsed = text;
    parsed = parsed.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    parsed = parsed.replace(/\*(.+?)\*/g, "<em>$1</em>");
    return parsed;
  };

  const getShortDescription = (text) => {
    if (!text) return "";
    const cleanText = String(text).trim();
    if (cleanText.length <= 100) return parseDescription(cleanText);
    return parseDescription(cleanText.substring(0, 100) + "...");
  };

  const handleDragStart = (e, promotionId) => {
    setDraggedId(promotionId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (promotionId) => {
    setDraggedOverId(promotionId);
  };

  const handleDragLeave = () => {
    setDraggedOverId(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    setDraggedOverId(null);

    if (draggedId === targetId) return;

    const draggedIndex = localPromotions.findIndex((p) => p.id === draggedId);
    const targetIndex = localPromotions.findIndex((p) => p.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newPromotions = [...localPromotions];
    const [draggedItem] = newPromotions.splice(draggedIndex, 1);
    newPromotions.splice(targetIndex, 0, draggedItem);

    setLocalPromotions(newPromotions);
    setHasChanges(true);
    setDraggedId(null);
  };

  const handleSaveOrder = async () => {
    try {
      const promotionIds = localPromotions.map((promotion) => promotion.id);

      await updatePromotionsOrder({
        variables: {
          ids: promotionIds,
        },
      });

      notifications.show({
        color: "green",
        title: "Порядок акций успешно сохранен",
      });

      setHasChanges(false);
      refetch();
    } catch (err) {
      console.error(err);
      notifications.show({
        color: "red",
        title: "Ошибка при сохранении порядка",
      });

      const sorted = [...promotions].sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        return orderA - orderB;
      });
      setLocalPromotions(sorted);
      setHasChanges(false);
    }
  };

  const handleDeletePromotion = async () => {
    if (!selectedPromotion) return;

    try {
      await deletePromotion({
        variables: {
          where: { id: selectedPromotion.id },
        },
      });

      notifications.show({
        color: "green",
        title: "Акция успешно удалена",
      });

      setDeleteModalOpen(false);
      setSelectedPromotion(null);
      refetch();
    } catch (err) {
      console.error(err);
      notifications.show({
        color: "red",
        title: "Ошибка при удалении акции",
      });
    }
  };

  const handleToggleStatus = async (promotion) => {
    try {
      await updatePromotion({
        variables: {
          where: { id: promotion.id },
          data: { active: !promotion.active },
        },
      });

      notifications.show({
        color: "green",
        title: `Акция ${!promotion.active ? "активирована" : "деактивирована"}`,
      });

      refetch();
    } catch (err) {
      console.error(err);
      notifications.show({
        color: "red",
        title: "Ошибка при обновлении статуса",
      });
    }
  };

  const openDeleteModal = (promotion) => {
    setSelectedPromotion(promotion);
    setDeleteModalOpen(true);
  };

  const remainingSlots = MAX_PROMOTIONS - promotions.length;

  return (
    <Container>
      <Header>
        <Group justify="space-between" align="center" mb={24}>
          <div>
            <Text fw="600" size="lg">
              Акции
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              {promotions.length} из {MAX_PROMOTIONS} акций добавлено
            </Text>
          </div>

          <Tooltip
            label={
              canAddMore
                ? "Добавить новую акцию"
                : `Максимум достигнут (${MAX_PROMOTIONS} акций)`
            }
            disabled={canAddMore}
          >
            <Button
              onClick={() => navigate("/promotions/create")}
              disabled={!canAddMore}
              leftSection={<IconPlus size={16} />}
            >
              Добавить акцию
            </Button>
          </Tooltip>
        </Group>

        <ProgressBar>
          <ProgressFill
            percentage={(promotions.length / MAX_PROMOTIONS) * 100}
          />
        </ProgressBar>

        {remainingSlots > 0 && (
          <Text size="xs" c="dimmed" mt={8}>
            Осталось свободных мест: {remainingSlots}
          </Text>
        )}

        {hasChanges && (
          <InfoPanel mt={16}>
            <Group justify="space-between">
              <Text size="sm" c="blue">
                💡 Вы изменили порядок баннеров. Нажмите кнопку ниже, чтобы
                сохранить
              </Text>
              <Button size="sm" onClick={handleSaveOrder}>
                Сохранить порядок
              </Button>
            </Group>
          </InfoPanel>
        )}
      </Header>

      {loading && (
        <Center py={80}>
          <Loader />
        </Center>
      )}

      {!loading && promotions.length === 0 && (
        <EmptyState>
          <Text fw="600" size="lg" mb={8}>
            Нет акций
          </Text>
          <Text size="sm" c="dimmed">
            Нажмите кнопку выше, чтобы добавить первую акцию
          </Text>
        </EmptyState>
      )}

      {!loading && localPromotions.length > 0 && (
        <>
          <HintText>
            <IconGripVertical size={14} /> Перетащите карточку, чтобы изменить
            порядок баннеров
          </HintText>

          <PromotionsGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} gutter="md">
            {localPromotions.map((promotion) => (
              <PromotionCard
                key={promotion.id}
                draggable
                isDragged={draggedId === promotion.id}
                isDraggedOver={draggedOverId === promotion.id}
                onDragStart={(e) => handleDragStart(e, promotion.id)}
                onDragOver={handleDragOver}
                onDragEnter={() => handleDragEnter(promotion.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, promotion.id)}
              >
                <DragHandle
                  title="Перетащите для изменения порядка"
                  className="drag-handle"
                >
                  <IconGripVertical size={16} />
                </DragHandle>

                <StatusBadge
                  active={promotion.active}
                  size="sm"
                  variant="light"
                >
                  {promotion.active ? "✓ Активна" : "○ Неактивна"}
                </StatusBadge>

                <ImageWrapper>
                  <PromotionImage
                    src={`/uploads/${promotion.image}`}
                    alt={promotion.title}
                  />
                </ImageWrapper>

                <CardContent>
                  <PromotionTitle>{promotion.title}</PromotionTitle>
                  {promotion.endDate && (
                    <PromotionDate>
                      {formatDate(promotion.endDate)}
                    </PromotionDate>
                  )}
                  <PromotionDescription
                    dangerouslySetInnerHTML={{
                      __html: getShortDescription(promotion.description),
                    }}
                  />
                </CardContent>

                <ActionButtons>
                  <Tooltip label="Активировать/Деактивировать">
                    <ActionIcon
                      variant="light"
                      size="lg"
                      onClick={() => handleToggleStatus(promotion)}
                      color={promotion.active ? "green" : "gray"}
                    >
                      {promotion.active ? (
                        <IconToggleRight size={18} />
                      ) : (
                        <IconToggleLeft size={18} />
                      )}
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label="Редактировать">
                    <ActionIcon
                      variant="light"
                      size="lg"
                      onClick={() =>
                        navigate(`/promotions/${promotion.id}/update`)
                      }
                    >
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label="Удалить">
                    <ActionIcon
                      variant="light"
                      size="lg"
                      color="red"
                      onClick={() => openDeleteModal(promotion)}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                </ActionButtons>
              </PromotionCard>
            ))}
          </PromotionsGrid>
        </>
      )}

      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedPromotion(null);
        }}
        title="Удалить акцию?"
        centered
      >
        <Stack gap={16}>
          <Text size="sm" c="dimmed">
            Вы уверены, что хотите удалить акцию
            {selectedPromotion?.title?.trim()
              ? ` "${selectedPromotion.title}"`
              : ""}
            ? Это действие невозможно отменить.
          </Text>

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedPromotion(null);
              }}
            >
              Отмена
            </Button>
            <Button color="red" onClick={handleDeletePromotion}>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4c6ef5 0%, #7c3aed 100%);
  width: ${(props) => props.percentage}%;
  transition: width 0.3s ease;
  border-radius: 3px;
`;

const InfoPanel = styled.div`
  padding: 12px 16px;
  background-color: #e7f5ff;
  border: 1px solid #74c0fc;
  border-radius: 8px;
`;

const HintText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #868e96;
  margin-bottom: 16px;
`;

const PromotionsGrid = styled(SimpleGrid)`
  margin-top: 24px;
`;

const PromotionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
  border: 2px solid ${(props) => (props.isDraggedOver ? "#4c6ef5" : "#e9ecef")};
  border-radius: 8px;
  transition: all 0.2s ease;
  background: white;
  opacity: ${(props) => (props.isDragged ? 0.5 : 1)};
  cursor: ${(props) => (props.isDragged ? "grabbing" : "grab")};

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: ${(props) => (props.isDraggedOver ? "#4c6ef5" : "#dee2e6")};
  }
`;

const DragHandle = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 11;
  padding: 4px 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  color: #868e96;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 1);
    color: #4c6ef5;
  }

  &:active {
    cursor: grabbing;
  }
`;

const StatusBadge = styled(Badge)`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  background-color: ${(props) =>
    props.active
      ? "rgba(34, 197, 94, 0.1)"
      : "rgba(107, 114, 128, 0.1)"} !important;
  color: ${(props) => (props.active ? "#22c55e" : "#6b7280")} !important;
  font-weight: 600;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  background-color: #f9f9f9;
`;

const PromotionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${PromotionCard}:hover & {
    transform: scale(1.05);
  }
`;

const CardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PromotionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #ffd93d;
  line-height: 1.3;
`;

const PromotionDate = styled.p`
  margin: 0;
  font-size: 12px;
  color: #868e96;
  font-weight: 500;
`;

const PromotionDescription = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
    color: #495057;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid #e9ecef;
  background-color: #fafafa;
`;

const EmptyState = styled(Center)`
  flex-direction: column;
  padding: 80px 20px;
  text-align: center;
`;
